import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Info, PanelRightOpen, Pencil } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from 'src/shared/components/ui/dialog';
import Textarea from 'src/shared/components/textarea/Textarea';
import { Badge } from 'src/shared/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'src/shared/components/ui/sheet';
import ReadMore from 'src/shared/components/read-more/ReadMore';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import useAuthService from 'src/shared/hooks/useAuthService';

export type NatureStatusCardProps = {
    text: string | null;
    createdAt: string | null;
    isLoading: boolean;
    onSave: (text: string) => Promise<void>;
};

const NatureStatusCard = ({ text, createdAt, isLoading, onSave }: NatureStatusCardProps) => {
    const { isCurrentUserSuperAdmin } = useAuthService();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSheet, setShowSheet] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const handleEditClick = () => {
        setEditValue(text ?? '');
        setShowEditModal(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave(editValue);
            setShowEditModal(false);
        } finally {
            setIsSaving(false);
        }
    };

    const formattedDate = createdAt ? format(parseISO(createdAt), 'd MMM yyyy') : null;

    if (isLoading) {
        return (
            <div className='bg-white shadow-portfolio-summary-card rounded-lg p-4 flex flex-col gap-3 animate-pulse h-full'>
                <div className='h-4 bg-grey-20 rounded w-1/3' />
                <div className='h-3 bg-grey-20 rounded w-full' />
                <div className='h-3 bg-grey-20 rounded w-5/6' />
                <div className='h-3 bg-grey-20 rounded w-4/6' />
            </div>
        );
    }

    return (
        <div className='bg-white shadow-portfolio-summary-card rounded-lg p-4 flex flex-col gap-3 h-full'>
            <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                    <span className='text-[20px] font-normal text-primary'>Nature Status</span>
                    <QuestionButtonHelp type='api' slug='nature-status' placement='top' icon={<Info className='h-4 w-4 text-primary' />} />
                    {formattedDate && <span className='text-xs text-muted-foreground'>Updated {formattedDate}</span>}
                </div>
                <div className='flex items-center gap-1'>
                    <Badge variant='secondary'>AI Assisted</Badge>
                    {isCurrentUserSuperAdmin && (
                        <button
                            type='button'
                            data-testid='edit-btn'
                            className='border border-border rounded-lg p-1 text-xs text-foreground flex items-center'
                            onClick={handleEditClick}
                        >
                            <Pencil className='h-4 w-4' />
                            <span className='sr-only'>Edit nature status</span>
                        </button>
                    )}
                    {text && (
                        <button
                            type='button'
                            data-testid='read-more-btn'
                            className='border border-border rounded-lg px-2 py-1 text-xs text-foreground flex items-center gap-1'
                            onClick={() => setShowSheet(true)}
                        >
                            Read more <PanelRightOpen className='h-3 w-3' />
                        </button>
                    )}
                </div>
            </div>

            {text ? (
                <ReadMore text={text} lines={7} modalTitle='Nature Status' onExpand={() => setShowSheet(true)} />
            ) : (
                <div className='flex items-center justify-center h-[130px] px-4 text-center'>
                    <p className='text-sm text-grey-80'>
                        Once your AI-assisted Nature Status is ready, it will appear here and provide a quick, actionable overview of this
                        location&apos;s local biodiversity health, operational impacts and natural capital dependencies.
                    </p>
                </div>
            )}

            <Dialog open={showEditModal} onOpenChange={open => !open && setShowEditModal(false)}>
                <DialogContent className='w-[600px] max-w-[600px]'>
                    <DialogHeader>
                        <DialogTitle className='text-xl text-primary font-normal'>Edit Nature Status</DialogTitle>
                        <DialogDescription className='sr-only'>Edit the nature status text for this location</DialogDescription>
                    </DialogHeader>
                    <Textarea
                        className='h-[200px]'
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        placeholder='Describe the nature status...'
                    />
                    <DialogFooter>
                        <SecondaryButton onClick={() => setShowEditModal(false)}>Cancel</SecondaryButton>
                        <PrimaryButton onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </PrimaryButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Sheet open={showSheet} onOpenChange={setShowSheet}>
                <SheetContent className='flex flex-col'>
                    <SheetHeader>
                        <SheetTitle>Nature Status</SheetTitle>
                    </SheetHeader>
                    <div className='text-sm text-black whitespace-pre-wrap overflow-y-auto flex-1'>{text}</div>
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default NatureStatusCard;
